import React, { useState } from 'react';
import { Check, KeyRound, ListChecks, Loader2, Pencil } from 'lucide-react';
import { Card } from './ui';

// Cadre du projet : quelques puces posées avant le kickoff, en plus des règles de l'organisation (décisions
// réservées…) — règles propres au projet, précisions, limites. Lu par tous, écrit par le titulaire de la roadmap.

const FrameCard = ({ frame, roadmap, onSave, title = 'Cadre du projet', hint = null }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const items = frame?.items || [];
  const can = roadmap ? roadmap.can !== false : true;
  const holders = roadmap?.holders || [];
  if (!frame) return null;
  if (!items.length && !can) return null;

  const open = () => { setDraft(items.join('\n')); setEditing(true); };
  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft.split('\n').map((line) => line.replace(/^\s*[-•*]\s*/, '').trim()).filter(Boolean));
      setEditing(false);
    } catch {
      // erreur affichée dans le bandeau de la fiche
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title={title} icon={ListChecks}
      action={can && !editing ? (
        <button type="button" onClick={open} className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-indigo-700">
          <Pencil size={12} /> {items.length ? 'Modifier' : 'Poser le cadre'}
        </button>
      ) : null}>
      {editing ? (
        <div className="space-y-2">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={Math.max(4, items.length + 2)} autoFocus
            aria-label="Cadre du projet, une puce par ligne"
            placeholder={'Une puce par ligne, par exemple :\nToute évolution du périmètre passe par le leader du projet\nLes essais se font sur le banc du client, pas chez nous'}
            className="w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-sm leading-relaxed focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-50" />
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] text-slate-400">Une puce par ligne. Les règles de l’organisation (décisions réservées…) s’appliquent déjà.</p>
            <div className="flex shrink-0 gap-1">
              <button type="button" onClick={() => setEditing(false)} className="rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-slate-100">Annuler</button>
              <button type="button" onClick={save} disabled={saving}
                className="flex items-center gap-1 rounded-md bg-slate-900 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-40">
                {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Enregistrer
              </button>
            </div>
          </div>
        </div>
      ) : items.length ? (
        <ul className="space-y-1.5">
          {items.map((item) => (
            <li key={item} className="flex gap-2 text-[13px] leading-snug text-slate-700">
              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
              <span className="min-w-0 flex-1">{item}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-400">
          {hint || 'Pas encore de cadre : les règles propres à ce projet et les précisions utiles, en plus des décisions réservées de l’organisation.'}
        </p>
      )}
      {!editing && !can && holders.length > 0 && (
        <p className="mt-2 flex items-center gap-1 text-[11px] text-violet-700">
          <KeyRound size={10} /> posé par {holders.map((h) => h.name).join(', ')}
        </p>
      )}
    </Card>
  );
};

export default FrameCard;
